import CategoryScores from "./CategoryScores";
import ScoreInfo from "./ScoreInfo";
import * as S from "./styles";
import { TrackingEvents, ProductNames } from "@local-logic/core/tracking";
import { Select } from "@local-logic/core/ui";
import {
  type scoreCategoriesType,
  type ScoreCategories,
  ScoreCategory as SC,
} from "@local-logic/elements-react";
import React, { useState, useEffect } from "react";
import { useTracking } from "~/context/trackingProvider";
import useIsMobile from "~/hooks/useIsMobile";
import useRankedCategories from "~/hooks/useRankedCategories";
import { useLocalContent } from "~/sdks/LocalContent/context";

const MobileScores = React.forwardRef<HTMLDivElement>((props, forwardedRef) => {
  const { activeCategory, setActiveCategory } = useLocalContent();
  const tracking = useTracking();
  const isMobile = useIsMobile();
  const { data } = useRankedCategories();
  const [rankedCategories, setRankedCategories] = useState(data);

  useEffect(() => {
    // The reason we're setting our own state is to trigger a re-render
    // If we're just using the "data" directly, setting the new active
    // category is completed but no re-render is done, showing the old
    // active category instead
    setRankedCategories(data);

    if (data.length > 0) {
      setActiveCategory(data[0].value);
    }
  }, [data]);

  useEffect(() => {
    // Don't track until we finished ranking the categories
    if (rankedCategories.length === 0) {
      return;
    }

    tracking.postEvent({
      eventType: TrackingEvents.LOCALCONTENT_CATEGORY_VIEWED,
      product: ProductNames.LOCAL_CONTENT,
      isMobile,
      eventAttributes: [
        {
          label: "activeCategory",
          data: activeCategory,
        },
      ],
    });
  }, [activeCategory]);

  const handleCategoryChanged = (category: ScoreCategories) => {
    setActiveCategory(category);

    tracking.postEvent({
      eventType: TrackingEvents.LOCALCONTENT_CLICKED,
      product: ProductNames.LOCAL_CONTENT,
      isMobile,
      eventAttributes: [
        {
          label: "activeCategory",
          data: category,
        },
      ],
    });
  };

  return (
    <S.ScoresContainer ref={forwardedRef}>
      <S.SelectContainer>
        <Select.Root
          value={activeCategory}
          onValueChange={(category: ScoreCategories) =>
            handleCategoryChanged(category)
          }
        >
          <Select.Trigger size="large">
            <SC.Root
              key={activeCategory}
              options={{ scoreCategory: activeCategory }}
            >
              <SC.Title
                options={{
                  color: "inherit",
                  noMargin: true,
                  variant: "h6",
                  lineHeight: 1,
                }}
              />
            </SC.Root>
          </Select.Trigger>
          <Select.Content>
            {rankedCategories.map((category: scoreCategoriesType) => (
              <SC.Root
                key={category.value}
                options={{ scoreCategory: category.value }}
              >
                <Select.Item value={category.value}>
                  <SC.Title
                    options={{
                      color: "inherit",
                      noMargin: true,
                      variant: "h6",
                    }}
                  />
                </Select.Item>
              </SC.Root>
            ))}
          </Select.Content>
        </Select.Root>
      </S.SelectContainer>
      {rankedCategories.length > 0 && (
        <CategoryScores
          category={
            rankedCategories.find(
              (s: scoreCategoriesType) => s.value === activeCategory
            )!
          }
        />
      )}
      <ScoreInfo />
    </S.ScoresContainer>
  );
});

MobileScores.displayName = "MobileScores";

export default MobileScores;
